import { motion, useAnimation } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { useEffect, useState } from 'react'

const Slide2Equation = () => {
  const controls = useAnimation()
  const [ref, inView] = useInView({
    threshold: 0.3,
    triggerOnce: false
  })

  const [activeLetter, setActiveLetter] = useState(null)

  const letters = [
    {
      combination: "QU",
      title: "QUANTUM MECHANICS",
      philosophy: "Linear growth is for the past. We operate on Quantum principles.",
      logic: "In physics, a quantum leap isn't a step—it's an instant jump between states of energy. We engineer systems that allow your business to exist in two states at once: Stability and Hyper-Growth."
    },
    {
      combination: "A",
      title: "ARTIFICIAL INTELLIGENCE",
      philosophy: "Intelligence is no longer biological. It is synthetic.",
      logic: "We don't just 'use' AI tools. We integrate Synthetic Intelligence into the nervous system of your brand. Like a neural network, our solutions learn, adapt, and evolve faster than any human workforce."
    },
    {
      combination: "V",
      title: "VISION",
      philosophy: "You cannot hit a target you cannot see.",
      logic: "Vision isn't imagination; it is simulation. We use data to simulate the future before it happens. While others react to the market, we architect it."
    },
    {
      combination: "R",
      title: "REVOLUTION",
      philosophy: "To build the new, you must disrupt the old.",
      logic: "'R' stands for the kinetic energy required to break the status quo. We provide the digital arsenal for the Disruptors to overthrow the Defenders."
    },
    {
      combination: "I",
      title: "INNOVATION (SILENT)",
      philosophy: "The most powerful forces in the universe are silent.",
      logic: "Gravity is silent. Light is silent. The human mind thinks in silence. We silenced the 'I' to remind ourselves: Don't talk about innovation. Be the innovation. We do the hard work in the dark so your success can shine in the light.",
      isSilent: true
    },
    {
      combination: "ON",
      title: "ALWAYS ON",
      philosophy: "The universe has no off switch. Neither do we.",
      logic: "In the digital age, downtime is death. 'ON' is not a status; it is a state of being. Your brand must be Omnipresent. Omnipotent. Immortal."
    }
  ]

  useEffect(() => {
    if (inView) {
      controls.start('visible')
    }
  }, [controls, inView])

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  }

  const letterVariants = {
    hidden: { scale: 0.5, opacity: 0 },
    visible: (custom) => ({
      scale: 1,
      opacity: 1,
      transition: {
        delay: custom * 0.2,
        duration: 0.5,
        ease: "backOut"
      }
    }),
    hover: {
      scale: 1.05,
      textShadow: "0 0 20px rgba(34, 211, 238, 0.8)",
      transition: { duration: 0.3 }
    },
    silent: {
      opacity: [1, 0.3, 1],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  }

  return (
    <div id="equation" className="relative min-h-screen w-full overflow-hidden bg-gradient-to-br from-gray-900 via-black to-gray-900" ref={ref}>
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, #2AFEB7 1px, transparent 0)`,
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-24">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={controls}
          className="relative z-10 max-w-7xl mx-auto"
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="text-center mb-10 sm:mb-12 md:mb-16 px-3 sm:px-0">
            <h2 className="text-3xl xs:text-4xl sm:text-5xl md:text-6xl font-bold mb-4 tracking-tight">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400">
                THE EQUATION
              </span>
            </h2>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-300 italic px-2 sm:px-0">
              "We didn't just name a company. We solved an equation."
            </p>
          </motion.div>

          {/* Letters Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-12 sm:mb-16 px-3 sm:px-0">
            {letters.map((letter, index) => (
              <motion.div
                key={index}
                custom={index}
                variants={letterVariants}
                animate={letter.isSilent ? "silent" : "visible"}
                whileHover="hover"
                onHoverStart={() => setActiveLetter(index)}
                onHoverEnd={() => setActiveLetter(null)}
                className={`p-4 sm:p-5 md:p-6 rounded-xl cursor-pointer transition-all duration-300 relative min-h-[280px] sm:min-h-[320px] ${
                  letter.isSilent 
                    ? 'border border-dashed border-cyan-400/30 bg-cyan-400/5'
                    : 'bg-gray-900/50 border border-gray-700/50'
                } ${
                  activeLetter === index 
                    ? 'ring-1 sm:ring-2 ring-cyan-400 ring-offset-1 sm:ring-offset-2 ring-offset-gray-900 transform scale-[1.02] sm:scale-105' 
                    : ''
                }`}
              >
                {/* Letter Combination */}
                <div className="mb-3 sm:mb-4">
                  <div className={`text-3xl xs:text-4xl sm:text-5xl md:text-6xl font-bold ${
                    letter.isSilent ? 'text-cyan-400/50' : 'text-cyan-400'
                  }`}>
                    [{letter.combination}]
                  </div>
                  <div className={`text-base sm:text-lg md:text-xl font-semibold mt-1 sm:mt-2 ${
                    letter.isSilent ? 'text-gray-500' : 'text-white'
                  }`}>
                    {letter.title}
                  </div>
                </div>

                {/* Philosophy */}
                <div className="mb-2 sm:mb-3">
                  <div className="text-xs text-cyan-400 uppercase tracking-wider mb-1">
                    The Philosophy
                  </div>
                  <p className={`text-sm leading-relaxed line-clamp-3 sm:line-clamp-none ${
                    letter.isSilent ? 'text-gray-400 italic' : 'text-gray-300'
                  }`}>
                    {letter.philosophy}
                  </p>
                </div>

                {/* Logic */}
                <div className="mt-auto">
                  <div className="text-xs text-cyan-400 uppercase tracking-wider mb-1">
                    The Logic
                  </div>
                  <p className={`text-xs sm:text-sm leading-relaxed line-clamp-4 sm:line-clamp-5 ${
                    letter.isSilent ? 'text-gray-500' : 'text-gray-400'
                  }`}>
                    {letter.logic}
                  </p>
                </div>

                {/* Silent I Special Effect */}
                {letter.isSilent && (
                  <motion.div
                    className="absolute inset-0 rounded-xl pointer-events-none"
                    animate={{
                      boxShadow: [
                        'inset 0 0 20px rgba(34, 211, 238, 0.1)',
                        'inset 0 0 40px rgba(34, 211, 238, 0.2)',
                        'inset 0 0 20px rgba(34, 211, 238, 0.1)'
                      ]
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  />
                )}
              </motion.div>
            ))}
          </div>

          {/* Explanation Text */}
          <motion.div 
            variants={itemVariants}
            className="max-w-5xl mx-auto text-center px-3 sm:px-4"
          >
            <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-700/50 p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl">
              <div className="text-cyan-400 text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4">
                Q + U + A + V + R + I + O + N = QUAVRION
              </div>
              <p className="text-gray-300 text-sm sm:text-base md:text-lg leading-relaxed mb-3 sm:mb-4">
                Each letter represents a fundamental principle of our engineering philosophy. 
                Together, they form a complete equation for digital transformation and market dominance.
              </p>
              <p className="text-gray-400 text-xs sm:text-sm md:text-base leading-relaxed">
                The silent 'I' is our commitment: Innovation without noise. Results without rhetoric. 
                Power without pretension.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  )
}

export default Slide2Equation